<template>
    <div class="stylesheet-edit">
        <slot :open="openModal">
            <button
                class="button button--blank stylesheet-edit__trigger"
                @click.prevent="openModal"
            >
                <IconPencil :fill="iconFill" />
            </button>
        </slot>

        <portal to="modals">
            <Modal
                v-if="opened"
                name="stylesheet-edit"
                modal-class="stylesheet-edit__modal"
                :title="$t(modalTitle)"
                @close="closeModal"
            >
                <template #content>
                    <form
                        id="stylesheet-edit"
                        class="stylesheet-edit__form"
                        @submit.prevent="submit"
                    >
                        <Grid v-if="'order' in schema" :gap-y="1">
                            <Column :span="12">
                                <FormField
                                    name="order"
                                    :error="error"
                                    :label="schema.order.label"
                                    :required="schema.order.required"
                                >
                                    <PaginatedResource
                                        :fetch-method="fetchOrders"
                                        :filter-fields="{ customer: workspaceCustomerId }"
                                    >
                                        <template #default="{ loading, resource: orders }">
                                            <CustomSelect
                                                v-model="order"
                                                :form-value-text="({ details: { name } }) => name"
                                                :options="orders"
                                                :disabled="loading"
                                                :popper-options="{ placement: 'top' }"
                                                paginate
                                                :paginate-amount="10"
                                                :empty-text="$t('crm.orders.empty')"
                                                :placeholder="$t('crm.orders.selectOrder')"
                                                with-filter
                                                width="100%"
                                            >
                                                <template #default="{ item: { details } }">
                                                    {{ details.name }}
                                                </template>
                                            </CustomSelect>
                                        </template>
                                    </PaginatedResource>
                                </FormField>
                            </Column>
                        </Grid>
                        <Grid v-if="'target' in schema" :gap-y="1">
                            <Column :span="12">
                                <FormField
                                    name="target"
                                    :error="error"
                                    :label="schema.target.label"
                                    :required="schema.target.required"
                                >
                                    <FormInput v-model="values.target" />
                                </FormField>
                            </Column>
                        </Grid>
                        <Grid v-if="'explanation' in schema" :gap-y="1">
                            <Column :span="12">
                                <FormField
                                    name="explanation"
                                    :error="error"
                                    :label="schema.explanation.label"
                                    :required="schema.explanation.required"
                                >
                                    <FormTextarea v-model="values.explanation" width="100%" />
                                </FormField>
                            </Column>
                        </Grid>
                        <Grid v-if="'time_code' in schema" :gap-y="1">
                            <Column :span="3">
                                <FormField
                                    name="time_code"
                                    :error="error"
                                    :label="schema.time_code.label"
                                    :required="schema.time_code.required"
                                >
                                    <NumberInput
                                        v-model="values.time_code"
                                        :step="0.1"
                                        with-input
                                    />
                                </FormField>
                            </Column>
                        </Grid>
                    </form>
                </template>
                <template #bottom="{ close }">
                    <div class="modal__confirm">
                        <button
                            v-t="'misc.cancel'"
                            class="button button button--gray modal__confirm-button"
                            @click.prevent="close"
                        />
                        <button
                            v-t="'misc.save'"
                            :class="['button modal__confirm-button', { 'is-disabled': !isValid }]"
                            type="submit"
                            form="stylesheet-edit"
                            :disabled="!isValid"
                        />
                    </div>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { fetchOrders } from '@/orders/api/orders';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { IconPencil } from '@speechki-frontend/shared/ui/icons';
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import {
    FormField,
    FormInput,
    FormTextarea,
    NumberInput,
    CustomSelect,
} from '@speechki-frontend/shared/ui/forms';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { mapState, mapGetters } from 'vuex';

const NEW_ITEM = {
    order: null,
    target: null,
    explanation: null,
    time_code: 0,
    customer: 0,
};

export default {
    name: 'StylesheetEdit',
    components: {
        Modal,
        IconPencil,
        Grid,
        Column,
        FormField,
        FormInput,
        FormTextarea,
        NumberInput,
        CustomSelect,
        PaginatedResource,
    },
    props: {
        customer: {
            type: [Number, String],
            default: 0,
        },
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        schema: {
            type: Object,
            default: () => ({}),
        },
        iconFill: {
            type: String,
            default: 'rgb(127, 127, 127)',
        },
        modalTitle: {
            type: String,
            default: 'crm.stylesheet.edit.title',
        },
    },
    emits: ['opened', 'closed', 'submit'],
    data() {
        return {
            opened: false,
            error: undefined,
            orders: undefined,
            values: { ...NEW_ITEM },
        };
    },
    computed: {
        ...mapState('workspaces', ['workspaces']),
        ...mapGetters(['isAdmin']),
        isValid() {
            return Object.entries(this.schema).every(
                ([name, { required }]) => !required || !!this.values[name]
            );
        },
    },
    watch: {
        opened(value) {
            this.values = {
                ...NEW_ITEM,
                ...this.modelValue,
            };

            if (!value || this.modelValue.id) return;
            if (!this.isAdmin) {
                const workspace = this.workspaces.findIndex(
                    ({ id }) => id === Number(this.customer)
                );

                if (workspace < 0) {
                    throw new Error('Invalid workspace');
                }
            }

            this.values.customer = this.customer;
        },
    },
    methods: {
        fetchOrders,
        openModal() {
            this.opened = true;
            this.$emit('opened');
        },
        closeModal() {
            this.opened = false;
            this.$emit('closed');
        },
        submit() {
            if (this.error) {
                console.log(this.error);
                return false;
            }
            this.closeModal();
            this.$emit('submit', this.values);
        },
    },
};
</script>

<style lang="scss" src="./StylesheetEdit.scss"></style>
